import { addActivityLog } from "../logs/actions";
import {
  STUDENTS,
  ATTENDANCE,
  IDX_STUDENTS,
  VALUES,
} from "../../constants/collections";
import {
  addStudentToSchool,
  removeApplicantFromSchool,
  removePendingApplicantFromSchool,
  incrementGradeLevelCount,
  decrementGradeLevelCount,
  setStudentEnrollmentInSchool,
} from "../../helpers/schools";

import {
  addSchoolToStudent,
  addSectionToStudent,
  setSectionClassPopulation,
  addSectionClassesToStudent,
} from "../../helpers/students";
import { addStudentToSection } from "../../helpers/sections";
import { asyncForEach } from "../../helpers/Utils";
import includes from "lodash/includes";
import map from "lodash/map";
import { getUserAccount } from "../../helpers/users";
import { addStudentToClass } from "../../helpers/classes";
import { createAdmissionApplication } from "../../helpers/admission";

export const QUERY_NEW_STUDENT_START = "QUERY_NEW_STUDENT_START",
  QUERY_NEW_STUDENT_SUCCESS = "QUERY_NEW_STUDENT_SUCCESS",
  QUERY_NEW_STUDENT_FAIL = "QUERY_NEW_STUDENT_FAIL",
  CREATE_STUDENT_START = "CREATE_STUDENT_START",
  CREATE_STUDENT_SUCCESS = "CREATE_STUDENT_SUCCESS",
  CREATE_STUDENT_FAIL = "CREATE_STUDENT_FAIL",
  FETCH_SECTION_STUDENTS_START = "FETCH_SECTION_STUDENTS_START",
  FETCH_SECTION_STUDENTS_SUCCESS = "FETCH_SECTION_STUDENTS_SUCCESS",
  FETCH_SECTION_STUDENTS_FAIL = "FETCH_SECTION_STUDENTS_FAIL",
  EDIT_STUDENT = "EDIT_STUDENT",
  FETCH_CLASS_STUDENTS_START = "FETCH_CLASS_STUDENTS_START",
  FETCH_CLASS_STUDENTS_SUCCESS = "FETCH_CLASS_STUDENTS_SUCCESS",
  FETCH_CLASS_STUDENTS_FAIL = "FETCH_CLASS_STUDENTS_FAIL",
  ADD_VALUES_MARK = "ADD_VALUES_MARK",
  ADD_EXISTING_STUDENTS = "ADD_EXISTING_STUDENTS",
  UNENROLL_STUDENTS = "UNENROLL_STUDENTS",
  ENROLL_BACKSUBJECT = "ENROLL_BACKSUBJECT",
  UNENROLL_BACKSUBJECT = "UNENROLL_BACKSUBJECT",
  ADMIT_EXISTING_STUDENTS = "ADMIT_EXISTING_STUDENTS",
  SOFT_DELETE_STUDENT_START = "SOFT_DELETE_STUDENT_START",
  SOFT_DELETE_STUDENT_SUCCESS = "SOFT_DELETE_STUDENT_SUCCESS",
  SOFT_DELETE_STUDENT_FAIL = "SOFT_DELETE_STUDENT_FAIL";

const queryNewStudentStart = () => ({
  type: QUERY_NEW_STUDENT_START,
});

const queryNewStudentSuccess = () => ({
  type: QUERY_NEW_STUDENT_SUCCESS,
});

const queryNewStudentFail = (error) => ({
  type: QUERY_NEW_STUDENT_FAIL,
  error,
});

export const queryNewStudent =
  ({ student, studentId, edit }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    dispatch(queryNewStudentStart());
    try {
      const db = getFirestore();
      const studentRef = db.collection(STUDENTS);

      // Only check LRN uniqueness when the student actually has a DepEd LRN.
      // When hasLRN===false the lrn field will be auto-generated on save — skip this gate.
      if (student.hasLRN !== false && student.lrn) {
        const studentLRNQuery = studentRef.where("lrn", "==", student.lrn);
        const studentLRNSnap = await studentLRNQuery.get();
        if (!edit && !studentLRNSnap.empty) {
          throw new Error("query-error-lrn");
        } else if (edit && !studentLRNSnap.empty) {
          const docIds = map(studentLRNSnap.docs, "id");
          if (!includes(docIds, studentId)) {
            throw new Error("query-error-lrn");
          }
        }
      }

      const studentIdxRef = db.collection(IDX_STUDENTS);
      const studentNameQuery = studentIdxRef.where(
        "keywords",
        "array-contains",
        `${student.firstName.toLowerCase()} ${student.middleName.toLowerCase()} ${student.lastName.toLowerCase()}&all&all`,
      );
      const studentNameSnap = await studentNameQuery.get();
      if (!edit && !studentNameSnap.empty) {
        throw new Error("query-error-fullname");
      } else if (edit && !studentNameSnap.empty) {
        const docIds = map(studentNameSnap.docs, "id");
        if (!includes(docIds, studentId)) {
          throw new Error("query-error-fullname");
        }
      }
      dispatch(queryNewStudentSuccess());
    } catch (error) {
      console.log(error);
      let errorBody = error;
      if (error.message === "query-error-lrn") {
        errorBody = {
          message: "That LRN / Student ID is already taken.",
          field: "lrn",
        };
      } else if (error.message === "query-error-fullname") {
        errorBody = {
          message: "That student's name is already taken.",
          field: "fullname",
        };
      }
      dispatch(queryNewStudentFail(errorBody));
    }
  };

const createNewStudentStart = (newStudent) => ({
  type: CREATE_STUDENT_START,
  newStudent,
});

const createNewStudentSuccess = () => ({
  type: CREATE_STUDENT_SUCCESS,
});

const createNewStudentFail = (error) => ({
  type: CREATE_STUDENT_FAIL,
  error: error,
});

export const createNewStudent =
  ({ student, school, sy, sectionId, studentId, edit }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    dispatch(
      createNewStudentStart(
        [
          student.firstName,
          student.middleName,
          student.lastName,
          student.extName,
        ].join(" "),
      ),
    );
    try {
      const db = getFirestore();
      const newStudentRef = !edit
        ? db.collection(STUDENTS).doc()
        : db.collection(STUDENTS).doc(studentId);

      // Auto-generate placeholder lrn for students without a DepEd LRN.
      // Firestore document ID is guaranteed globally unique — no counter needed.
      let studentData = { ...student };
      if (!edit && (student.hasLRN === false || !student.lrn)) {
        studentData = {
          ...studentData,
          lrn: newStudentRef.id,
          hasLRN: false,
        };
      } else if (student.lrn) {
        studentData = { ...studentData, hasLRN: true };
      }

      if (!edit) {
        await newStudentRef.set({
          ...studentData,
          createdOn: db.FieldValue.serverTimestamp(),
          lastUpdated: db.FieldValue.serverTimestamp(),
          deleted: false,
        });
      } else {
        await newStudentRef.set(
          {
            ...studentData,
            lastUpdated: db.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
      }

      dispatch(createNewStudentSuccess());

      if (!edit && school) {
        // Add document to subcollection "schools" if schoolID is present
        await Promise.all([
          addSchoolToStudent(newStudentRef.id, school, sy),
          addStudentToSchool(school, newStudentRef.id, sy, student.gender),
        ]);

        // Auto-Admit the new student for the current SY
        await createAdmissionApplication(newStudentRef.id, sy, "NEW", {
          admissionStatus: "ADMITTED",
          schoolId: school,
        });
      }

      if (!edit && sectionId) {
        await Promise.all([
          // Add student to subcollection "students" of section
          addStudentToSection(sectionId, newStudentRef.id, sy),
          // Add section to subcollection "sections" of student
          addSectionToStudent(newStudentRef.id, sectionId, sy),
          // Add section classes to subcollection "classes" of student, which includes adding student to the each class
          addSectionClassesToStudent(newStudentRef.id, sectionId, sy),
        ]);
      }

      const newStudentDoc = await newStudentRef.get();
      const newStudent = { ...newStudentDoc.data(), id: newStudentDoc.id };
      dispatch(
        addActivityLog({
          doc: newStudent,
          action: !edit ? "createNewStudent" : "updatedStudent",
          actor: !edit ? student.createdBy : student.updatedBy,
        }),
      );
    } catch (error) {
      dispatch(createNewStudentFail(error));
      throw error; // Re-throw to let the caller handle UI feedback
    }
  };

const fetchSectionStudentsStart = (section) => ({
  type: FETCH_SECTION_STUDENTS_START,
  section,
});

const fetchSectionStudentsSuccess = (section, students, populationStat) => ({
  type: FETCH_SECTION_STUDENTS_SUCCESS,
  students,
  section,
  populationStat,
});

const fetchSectionStudentsFail = (error) => ({
  type: FETCH_SECTION_STUDENTS_SUCCESS,
  error,
});

export const fetchSectionStudents =
  ({ sectionStudents, section, getAttendance, attendanceId }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch(fetchSectionStudentsStart(section));
    try {
      const db = getFirestore();
      const fetchedStudents = [];
      await asyncForEach(sectionStudents, async (studentId) => {
        const studentRef = db.collection(STUDENTS).doc(studentId);
        const studentSnap = await studentRef.get();
        if (studentSnap.exists && !studentSnap.data().deleted) {
          let student = { id: studentSnap.id, ...studentSnap.data() };
          if (getAttendance) {
            const studentAttendanceRef = db
              .collection(STUDENTS)
              .doc(studentId)
              .collection(ATTENDANCE)
              .doc(attendanceId);
            const studentAttendanceSnap = await studentAttendanceRef.get();
            let studentAttendance = null;
            if (studentAttendanceSnap.exists) {
              studentAttendance = {
                id: studentAttendanceSnap.id,
                ...studentAttendanceSnap.data(),
              };
            }
            student = { ...student, attendance: studentAttendance };
          }
          fetchedStudents.push(student);
        }
      });
      const sectionPopulation = setSectionClassPopulation(fetchedStudents);
      dispatch(
        fetchSectionStudentsSuccess(
          section,
          fetchedStudents,
          sectionPopulation,
        ),
      );
    } catch (error) {
      dispatch(fetchSectionStudentsFail(error));
    }
  };

const fetchClassStudentsStart = (subjectClass) => ({
  type: FETCH_CLASS_STUDENTS_START,
  subjectClass,
});

const fetchClassStudentsSuccess = (subjectClass, students) => ({
  type: FETCH_CLASS_STUDENTS_SUCCESS,
  subjectClass,
  students,
});

const fetchClassStudentsFail = (error) => ({
  type: FETCH_CLASS_STUDENTS_FAIL,
  error,
});

export const fetchClassStudents =
  ({ classRecord, classId, includeUserAcct }) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch(fetchClassStudentsStart(classId));
    try {
      const db = getFirestore();
      const fetchedStudents = [];
      await asyncForEach(classRecord, async (record) => {
        const recordId = !includeUserAcct ? record.id : record;
        const studentRef = db.collection(STUDENTS).doc(recordId);
        const studentSnap = await studentRef.get();
        if (studentSnap.exists && !studentSnap.data().deleted) {
          const recordData = !includeUserAcct
            ? record
            : { userAccount: await getUserAccount(studentSnap.id, "student") };
          const student = {
            id: studentSnap.id,
            ...studentSnap.data(),
            ...recordData,
          };
          fetchedStudents.push(student);
        }
      });
      dispatch(fetchClassStudentsSuccess(classId, fetchedStudents));
    } catch (error) {
      dispatch(fetchClassStudentsFail(error));
    }
  };

export const addValuesMark =
  ({ studentId, sy, marks, quarter }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    try {
      const db = getFirestore();
      const studentValuesRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection(VALUES)
        .doc(sy);

      await studentValuesRef.set(
        {
          marks: {
            [quarter]: marks,
          },
        },
        { merge: true },
      );

      dispatch({ type: ADD_VALUES_MARK });
    } catch (error) {
      throw error.message;
    }
  };

export const addExistingStudents =
  ({ students, schoolId, sy, method, sectionId }) =>
  async (dispatch, getState, { getFirestore }) => {
    try {
      const db = getFirestore();
      async function processAdding(studentId, gender) {
        // Check Billing Status and Upgrade to ENROLLED if applicable
        const enrollmentRef = db
          .collection(STUDENTS)
          .doc(studentId)
          .collection("enrollments")
          .doc(sy);

        const enrollmentSnap = await enrollmentRef.get();
        if (enrollmentSnap.exists) {
          const data = enrollmentSnap.data();
          if (
            data.billingStatus === "PARTIAL" ||
            data.billingStatus === "CLEARED"
          ) {
            await enrollmentRef.set(
              {
                admissionStatus: "ENROLLED",
                lastUpdated: db.FieldValue.serverTimestamp(),
              },
              { merge: true },
            );
          }
        }

        // Add document to subcollection "schools" if schoolID is present
        await addSchoolToStudent(studentId, schoolId, sy);
        await addStudentToSchool(schoolId, studentId, sy, gender);
        await removeApplicantFromSchool(schoolId, studentId, sy); // Clean up bucket

        // Add student to subcollection "students" of section
        await addStudentToSection(sectionId, studentId, sy);
        // Add section to subcollection "sections" of student
        await addSectionToStudent(studentId, sectionId, sy);
        // Add section classes to subcollection "classes" of student, which includes adding student to the each class
        await addSectionClassesToStudent(studentId, sectionId, sy);
      }

      if (method === "fromSY") {
        // students here is the SY_SECTIONID
        let studentsRef = db
          .collection(IDX_STUDENTS)
          .where("sy_sections", "array-contains", students);

        const querySnap = await studentsRef.get();
        if (!querySnap.empty) {
          querySnap.forEach((doc) => {
            const student = doc.data();
            return processAdding(doc.id, student.gender);
          });
        }
      } else {
        // students here is array of value-label collection
        students.forEach((students) => {
          processAdding(students.value, students.gender);
        });
      }
      dispatch({ type: ADD_EXISTING_STUDENTS });
    } catch (error) {
      throw error;
    }
  };

export const unenrollStudents =
  ({ students, sy, section, school }) =>
  async (dispatch, getState, { getFirestore }) => {
    try {
      const db = getFirestore();

      const processUnenrollment = async (student) => {
        // 1. Unenroll from Section/School Arrays
        await addStudentToSection(section, student.id, sy, true);
        await addSectionToStudent(student.id, section, sy, true);
        await addSectionClassesToStudent(student.id, section, sy, true);
        await addSectionClassesToStudent(student.id, section, sy, true);
        await setStudentEnrollmentInSchool(school, student.id, sy, false);

        // 2. Revert Admission Status to ADMITTED
        // We do this to ensure they can be re-enrolled later or their status is accurate
        const enrollmentRef = db
          .collection(STUDENTS)
          .doc(student.id)
          .collection("enrollments")
          .doc(sy);

        await enrollmentRef.set(
          {
            admissionStatus: "ADMITTED",
            lastUpdated: db.FieldValue.serverTimestamp(),
            ...(student.gradeLevel ? { gradeLevel: student.gradeLevel } : {}),
          },
          { merge: true },
        );
      };

      const promises = students.map((student) => processUnenrollment(student));
      await Promise.all(promises);

      dispatch({ type: UNENROLL_STUDENTS });
    } catch (error) {
      throw error;
    }
  };

export const enrollBackSubject =
  ({ students, sy, method, classId, unenroll }) =>
  async (dispatch, getState, { getFirestore }) => {
    try {
      const db = getFirestore();
      async function processAdding(studentId, gender) {
        await addStudentToClass(classId, studentId, sy, unenroll);
      }

      if (method === "fromSY") {
        // students here is the SY_SECTIONID
        let studentsRef = db
          .collection(IDX_STUDENTS)
          .where("sy_sections", "array-contains", students);

        const querySnap = await studentsRef.get();
        if (!querySnap.empty) {
          querySnap.forEach((doc) => processAdding(doc.id));
        }
      } else {
        // students here is array of value-label collection
        students.forEach((student) => {
          const enrolledStudent = unenroll ? student : student.value;
          processAdding(enrolledStudent);
        });
      }
      dispatch({ type: unenroll ? UNENROLL_BACKSUBJECT : ENROLL_BACKSUBJECT });
    } catch (error) {
      throw error;
    }
  };

export const admitExistingStudents =
  ({ students, sy, method, schoolId }) =>
  async (dispatch, getState, { getFirestore }) => {
    try {
      const db = getFirestore();
      async function processAdmitting(studentId) {
        // Create admission application with status ADMITTED
        await createAdmissionApplication(studentId, sy, "RETURNEE", {
          admissionStatus: "ADMITTED",
          schoolId: schoolId,
        });

        if (schoolId) {
          // Transition: Applicant -> Enrolled Student
          // 1. Add to Roster (students array)
          // Note: gender is needed here. We need to fetch student gender if not available.
          // For now, let's assume 'Male' default or fetch it.
          // Optimization: If students array has gender, we use it. If from "fromSY", we have it.
          // If from "select", we might default.
          // Ideally we fetch student to get gender.
          const db = getFirestore();
          const studentSnap = await db
            .collection(STUDENTS)
            .doc(studentId)
            .get();
          const studentData = studentSnap.data();
          const gender = studentData?.gender || "Male";

          await Promise.all([
            addStudentToSchool(schoolId, studentId, sy, gender),
            removeApplicantFromSchool(schoolId, studentId, sy),
            addSchoolToStudent(studentId, schoolId, sy),
          ]);
        }
      }

      if (method === "fromSY") {
        // students here is the SY_SECTIONID
        let studentsRef = db
          .collection(IDX_STUDENTS)
          .where("sy_sections", "array-contains", students);

        const querySnap = await studentsRef.get();
        if (!querySnap.empty) {
          const promises = [];
          querySnap.forEach((doc) => {
            promises.push(processAdmitting(doc.id));
          });
          await Promise.all(promises);
        }
      } else {
        // students here is array of value-label collection
        const promises = students.map((student) =>
          processAdmitting(student.value),
        );
        await Promise.all(promises);
      }
      dispatch({ type: ADMIT_EXISTING_STUDENTS });
    } catch (error) {
      throw error;
    }
  };

export const approveStudentApplication =
  ({ studentId, schoolId, sy, status = "ADMITTED" }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const approvedBy = auth ? auth.uid : "system";

      // 1. Update Admission Status to ADMITTED/ENROLLED and Log Approval
      // We set type to 'NEW' (or keep existing) and status ADMITTED.

      const enrollmentRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments") // Assuming 'enrollments' is the collection name used in helpers. Check src/helpers/admission.js if needed.
        .doc(sy);

      const updateData = {
        admissionStatus: status,
        billingStatus: status === "ENROLLED" ? "BILLED" : "PENDING_ASSESSMENT",
        schoolYear: sy,
        studentId: studentId,
        sectionId: null,
        requirements: {},
        audit: {
          updated: db.FieldValue.serverTimestamp(),
          statusAuth: db.FieldValue.serverTimestamp(), // Auth timestamp for status change
        },
        approvedBy: approvedBy,
        approvedAt: db.FieldValue.serverTimestamp(),
        lastUpdated: db.FieldValue.serverTimestamp(),
        schoolId: schoolId,
      };

      await enrollmentRef.set(updateData, { merge: true });


      // 2. Transition Applicant -> Enrolled Student Arrays
      // Fetch student to get gender
      const studentSnap = await db.collection(STUDENTS).doc(studentId).get();
      const studentData = studentSnap.data();
      const gender = studentData?.gender || "Male";

      const promises = [
        addStudentToSchool(schoolId, studentId, sy, gender),
        removePendingApplicantFromSchool(schoolId, studentId, sy),
        addSchoolToStudent(studentId, schoolId, sy),
      ];

      await Promise.all(promises);

      dispatch(
        addActivityLog({
          doc: { id: studentId, ...studentData },
          action: "approveApplication",
          actor: approvedBy,
        }),
      );
    } catch (error) {
      throw error;
    }
  };

export const updateStudentBillingStatus =
  ({ studentId, sy, status, schoolId }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const updatedBy = auth ? auth.uid : "system";

      const enrollmentRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments")
        .doc(sy);

      await enrollmentRef.set(
        {
          billingStatus: status,
          audit: {
            updated: db.FieldValue.serverTimestamp(),
          },
          lastUpdated: db.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );

      dispatch(
        addActivityLog({
          doc: { id: studentId },
          action: "updatedBillingStatus",
          actor: updatedBy,
          meta: { status, sy },
        }),
      );
    } catch (error) {
      throw error;
    }
  };

export const updateStudentBillingDetails =
  ({ studentId, sy, schoolId, billingItems, billingStatus }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const updatedBy = auth ? auth.uid : "system";

      const enrollmentRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments")
        .doc(sy);

      await enrollmentRef.set(
        {
          billingItems,
          billingStatus,
          audit: {
            billingUpdated: db.FieldValue.serverTimestamp(),
          },
          lastUpdated: db.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );

      dispatch(
        addActivityLog({
          doc: { id: studentId },
          action: "updatedBillingDetails",
          actor: updatedBy,
          meta: { billingStatus, sy },
        }),
      );
    } catch (error) {
      throw error;
    }
  };

export const assignStudentGradeLevel =
  ({ studentId, sy, schoolId, gradeLevel, prevGradeLevel }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();

      // 1. Update Student Grade Level for this SY
      await db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("gradeLevels") // Ensure this matches what BillingStatement uses
        .doc(sy)
        .set({
          gradeLevel,
          updatedAt: db.FieldValue.serverTimestamp(),
        });

      // 2. Update Student Main Doc (optional but good for denormalization)
      await db
        .collection(STUDENTS)
        .doc(studentId)
        .set({ gradeLevel }, { merge: true });

      // 3. Update School Grade Counters
      if (schoolId) {
        const promises = [];
        if (prevGradeLevel && prevGradeLevel !== gradeLevel) {
          promises.push(decrementGradeLevelCount(schoolId, sy, prevGradeLevel));
        }
        if (gradeLevel && gradeLevel !== prevGradeLevel) {
          promises.push(incrementGradeLevelCount(schoolId, sy, gradeLevel));
        }
        await Promise.all(promises);
      }
    } catch (error) {
      console.error("Assign Grade Level Error:", error);
      throw error;
    }
  };

export const confirmEnrollment =
  ({ studentId, sy, schoolId }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const updatedBy = auth ? auth.uid : "system";

      const enrollmentRef = db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments")
        .doc(sy);

      // Verify that this enrollment belongs to the current user (if student)
      // or that the user is authorized. ideally rules handle this, but logical check is good.
      if (auth.uid !== studentId) {
        // simple check, but in real app roles should be checked.
        // for Wizard context, we assume user is confirming their own.
        // We will rely on Rules for hard security.
      }

      await enrollmentRef.set(
        {
          admissionStatus: "ASSESSED",
          lastUpdated: db.FieldValue.serverTimestamp(),
          audit: {
            enrollmentConfirmed: db.FieldValue.serverTimestamp(),
          },
        },
        { merge: true },
      );

      dispatch(
        addActivityLog({
          doc: { id: studentId },
          action: "confirmedEnrollment",
          actor: updatedBy,
          meta: { sy },
        }),
      );
    } catch (error) {
      throw error;
    }
  };

export const enrollStudentsToSection =
  ({ students, sy, schoolId, sectionId, gradeLevel }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    try {
      const db = getFirestore();

      const processEnrollment = async (studentId, gender) => {
        // 1. Update Admission Status to ENROLLED
        // This implicitly assumes billing/status checks were done by the UI/Search
        const enrollmentRef = db
          .collection(STUDENTS)
          .doc(studentId)
          .collection("enrollments")
          .doc(sy);

        await enrollmentRef.set(
          {
            admissionStatus: "ENROLLED",
            lastUpdated: db.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );

        // 2. Set as Enrolled in School
        await setStudentEnrollmentInSchool(schoolId, studentId, sy, true);

        // 3. Add to Section & Classes
        await addStudentToSection(sectionId, studentId, sy);
        await addSectionToStudent(studentId, sectionId, sy);
        await addSectionClassesToStudent(studentId, sectionId, sy);

        // 4. Update Grade Level (if not set or mismatch?)
        // The user requirement says "same grade level as section", so we enforce it.
        await db
          .collection(STUDENTS)
          .doc(studentId)
          .collection("gradeLevels")
          .doc(sy)
          .set(
            {
              gradeLevel, // Ensure grade level matches section
              updatedAt: db.FieldValue.serverTimestamp(),
            },
            { merge: true },
          );

        // Also update denormalized
        await db
          .collection(STUDENTS)
          .doc(studentId)
          .set({ gradeLevel }, { merge: true });
      };

      const promises = students.map((bg) =>
        processEnrollment(bg.value, bg.gender),
      );

      await Promise.all(promises);

      dispatch({ type: ADD_EXISTING_STUDENTS }); // Reuse type for refresh
    } catch (error) {
      throw error;
    }
  };

export const softDeleteStudent =
  ({ studentId, sy, schoolId }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    dispatch({ type: SOFT_DELETE_STUDENT_START });
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const deletedBy = auth ? auth.uid : "system";

      // 1. Soft Delete Main Doc (SKIPPED for Contextual Delete)
      await db.collection(STUDENTS).doc(studentId).update({
        deleted: true,
        lastUpdated: db.FieldValue.serverTimestamp(),
      });

      // 2. Soft Delete Enrollment (Trigger Logic)
      await db
        .collection(STUDENTS)
        .doc(studentId)
        .collection("enrollments")
        .doc(sy)
        .update({
          deleted: true,
          admissionStatus: "CANCELLED",
          billingStatus: "CANCELLED",
          lastUpdated: db.FieldValue.serverTimestamp(),
        });

      dispatch(
        addActivityLog({
          doc: { id: studentId },
          action: "softDeleteStudent",
          actor: deletedBy,
        }),
      );

      dispatch({ type: SOFT_DELETE_STUDENT_SUCCESS, studentId });
    } catch (error) {
      dispatch({ type: SOFT_DELETE_STUDENT_FAIL, error });
      throw error;
    }
  };
